/**
 * Local SPA URL unification.
 *
 * Official local asset-canister URL (ICP docs):
 *   http://{assets-canister-id}.localhost:8000/
 *
 * Legacy query form (absolute /assets/* break without ?canisterId=):
 *   http://127.0.0.1:8000/?canisterId={id}
 *
 * Redirect query-form → subdomain so icons/JS load natively.
 */

export function isLocalReplicaNetwork(): boolean {
	try {
		const env = import.meta.env as Record<string, string | undefined>;
		const app = String(env.VITE_APP_ENV ?? env.MODE ?? "").toLowerCase();
		const icp = String(
			env.VITE_ICP_ENVIRONMENT ?? env.ICP_ENVIRONMENT ?? "",
		).toLowerCase();
		if (app === "local" || app === "development") return true;
		if (icp === "local" || icp === "development") return true;
		const host = String(env.VITE_IC_HOST ?? "");
		if (host.includes("127.0.0.1") || host.includes("localhost")) return true;
	} catch {
		/* ignore */
	}
	return false;
}

/** Extract canister id from `{id}.localhost` hostname. */
export function canisterIdFromLocalhostHostname(
	hostname: string,
): string | null {
	const h = hostname.toLowerCase();
	if (!h.endsWith(".localhost") && !h.endsWith(".localhost.")) return null;
	const id = h.replace(/\.localhost\.?$/, "");
	if (!id || id.includes(".")) return null;
	if (!id.includes("-")) return null;
	return id;
}

/**
 * On local replica, prefer official subdomain SPA URL so /assets/* works.
 * Redirects 127.0.0.1|localhost?canisterId=X → http://X.localhost:PORT/
 * Returns true if a redirect was initiated.
 */
export function maybeRedirectToCanisterLocalhost(): boolean {
	if (typeof window === "undefined") return false;
	if (!isLocalReplicaNetwork()) return false;

	const { hostname, port, protocol, pathname, search, hash } = window.location;
	const h = hostname.toLowerCase();

	// Already on {id}.localhost — stay
	if (canisterIdFromLocalhostHostname(h)) return false;

	// Only rewrite bare replica hosts
	if (h !== "127.0.0.1" && h !== "localhost" && h !== "[::1]" && h !== "0.0.0.0") {
		return false;
	}

	const params = new URLSearchParams(search);
	const canisterId = params.get("canisterId")?.trim();
	if (!canisterId || !canisterId.includes("-")) return false;

	// Port: keep current page port (usually 8000)
	const p =
		port ||
		(protocol === "https:" ? "443" : "80");
	const portPart = p === "80" || p === "443" ? "" : `:${p}`;

	// Drop canisterId from query when moving to subdomain
	params.delete("canisterId");
	const rest = params.toString();
	const qs = rest ? `?${rest}` : "";
	const target = `http://${canisterId}.localhost${portPart}${pathname}${qs}${hash}`;

	if (window.location.href === target) return false;
	window.location.replace(target);
	return true;
}

/**
 * @deprecated use maybeRedirectToCanisterLocalhost — subdomain is primary now
 */
export function maybeRedirectCanisterLocalhostToQueryForm(): boolean {
	return maybeRedirectToCanisterLocalhost();
}

/**
 * Local II derivation origin.
 * Prefer current SPA origin when on {assets}.localhost (official local URL).
 * Fallback 127.0.0.1 only when still on query form.
 */
export function resolveLocalDerivationOrigin(
	envDerivation?: string,
): string | undefined {
	if (envDerivation?.trim()) return envDerivation.trim();
	if (typeof window === "undefined") return undefined;
	if (!isLocalReplicaNetwork()) {
		return window.location.origin;
	}
	const sub = canisterIdFromLocalhostHostname(window.location.hostname);
	if (sub) {
		return window.location.origin;
	}
	// Query form / vite: pin to 127.0.0.1 for stability
	try {
		const host = String(
			(import.meta.env as Record<string, string | undefined>).VITE_IC_HOST ??
				"http://127.0.0.1:8000",
		);
		const u = new URL(host);
		return `http://127.0.0.1:${u.port || "8000"}`;
	} catch {
		return "http://127.0.0.1:8000";
	}
}
